import React, { useState, useEffect, useRef } from 'react';
import { apiFetch, useAuth, useToast } from '../../App';
import { CheckCircle2, Clock, Calendar, Loader2, FileText, Radio, Camera, Image } from 'lucide-react';
import LiveStreamHost from '../../components/LiveStreamHost';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const STATUS_COLORS = {
  REQUESTED: { bg: 'rgba(99,102,241,0.1)', color: '#6366F1' },
  CONFIRMED: { bg: 'rgba(245,158,11,0.1)', color: '#F59E0B' },
  IN_PROGRESS: { bg: 'rgba(59,130,246,0.1)', color: '#3B82F6' },
  COMPLETED: { bg: 'rgba(16,185,129,0.1)', color: '#10B981' },
  CANCELLED: { bg: 'rgba(239,68,68,0.1)', color: '#EF4444' },
};

export default function DenGroomerView() {
  const { user } = useAuth();
  const addToast = useToast();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('today');
  const [selected, setSelected] = useState(null);
  const [showStream, setShowStream] = useState(false);
  const [logForm, setLogForm] = useState({ groomingNotes: '', behaviorObservations: '', nextGroomingRecommendation: '', productsUsed: '', beforePhotoUrl: '', afterPhotoUrl: '' });
  const [savingLog, setSavingLog] = useState(false);
  const [existingLog, setExistingLog] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(null); // 'before' | 'after' | null
  const beforeInputRef = useRef(null);
  const afterInputRef = useRef(null);

  useEffect(() => { loadBookings(); }, []);

  const loadBookings = async () => {
    setLoading(true);
    try {
      const data = await apiFetch('/api/bookings');
      setBookings(data);
    } catch (e) {
      console.error('Failed to load bookings:', e);
      addToast(e.message || 'Failed to load bookings', 'error');
    }
    setLoading(false);
  };

  const today = new Date().toDateString();
  const filtered = bookings.filter(b => {
    if (tab === 'today') {
      const d = b.scheduledStart ? new Date(b.scheduledStart).toDateString() : null;
      return d === today && b.status !== 'CANCELLED';
    }
    if (tab === 'upcoming') return ['REQUESTED', 'CONFIRMED'].includes(b.status);
    if (tab === 'active') return b.status === 'IN_PROGRESS';
    return false;
  });

  const openDetail = async (b) => {
    setSelected(b);
    setShowStream(false);
    setLogForm({ groomingNotes: '', behaviorObservations: '', nextGroomingRecommendation: '', productsUsed: '', beforePhotoUrl: '', afterPhotoUrl: '' });
    setExistingLog(null);
    try {
      const log = await apiFetch(`/api/service-logs/${b.id}`);
      if (log?.id) {
        setExistingLog(log);
        setLogForm({
          groomingNotes: log.groomingNotes || '',
          behaviorObservations: log.behaviorObservations || '',
          nextGroomingRecommendation: log.nextGroomingRecommendation || '',
          productsUsed: (log.productsUsed || []).join(', '),
          beforePhotoUrl: log.beforePhotoUrl || '',
          afterPhotoUrl: log.afterPhotoUrl || '',
        });
      }
    } catch (e) {
      console.error('Failed to load existing service log:', e);
    }
  };

  const updateBookingStatus = async (id, status) => {
    try {
      await apiFetch(`/api/bookings/${id}/status`, { method: 'PATCH', body: JSON.stringify({ status }) });
      setBookings(prev => prev.map(b => b.id === id ? { ...b, status } : b));
      if (selected?.id === id) setSelected({ ...selected, status });
      addToast(`Booking marked as ${status}`, 'success');
    } catch (e) {
      addToast(e.message, 'error');
    }
  };

  const saveLog = async () => {
    if (!selected) return;
    setSavingLog(true);
    try {
      const log = await apiFetch('/api/service-logs', {
        method: 'POST',
        body: JSON.stringify({
          bookingId: selected.id,
          groomingNotes: logForm.groomingNotes,
          behaviorObservations: logForm.behaviorObservations,
          nextGroomingRecommendation: logForm.nextGroomingRecommendation,
          productsUsed: logForm.productsUsed.split(',').map(s => s.trim()).filter(Boolean),
          beforePhotoUrl: logForm.beforePhotoUrl,
          afterPhotoUrl: logForm.afterPhotoUrl,
        }),
      });
      setExistingLog(log);
      addToast('Service log saved!', 'success');
    } catch (e) {
      addToast(e.message, 'error');
    }
    setSavingLog(false);
  };

  const uploadPhoto = async (file, type) => {
    setUploadingPhoto(type);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const token = localStorage.getItem('petfolk_token');
      const res = await fetch(`${API_URL}/api/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (!res.ok) throw new Error('Upload failed');
      const data = await res.json();
      setLogForm(f => ({ ...f, [type === 'before' ? 'beforePhotoUrl' : 'afterPhotoUrl']: data.url }));
      addToast(`${type === 'before' ? 'Before' : 'After'} photo uploaded!`, 'success');
    } catch (e) {
      addToast(e.message, 'error');
    }
    setUploadingPhoto(null);
  };

  return (
    <div data-testid="den-groomer-view">
      <div className="mb-6">
        <h1 className="font-outfit text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>My Sessions</h1>
        <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Groomer dashboard for {user?.name}</p>
      </div>

      {/* Tab Bar */}
      <div className="flex gap-2 mb-6 p-1 rounded-2xl w-fit" style={{ background: 'var(--bg-secondary)' }}>
        {[{ k: 'today', l: "Today" }, { k: 'upcoming', l: 'Upcoming' }, { k: 'active', l: '🔴 Active' }].map(t => (
          <button
            key={t.k}
            onClick={() => setTab(t.k)}
            className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all`}
            style={tab === t.k ? { background: 'var(--petfolk-orange)', color: 'white' } : { color: 'var(--text-secondary)' }}
          >
            {t.l}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 size={28} className="animate-spin text-petfolk-orange" /></div>
      ) : filtered.length === 0 ? (
        <div className="petfolk-card p-10 text-center">
          <Calendar size={40} className="mx-auto mb-3" style={{ color: 'var(--text-secondary)', opacity: 0.4 }} />
          <p style={{ color: 'var(--text-secondary)' }}>No sessions for this view</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(b => {
            const s = STATUS_COLORS[b.status] || STATUS_COLORS.REQUESTED;
            return (
              <div key={b.id} className="petfolk-card p-4 cursor-pointer hover:shadow-md transition-all" onClick={() => openDetail(b)} data-testid={`session-${b.id}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-outfit font-bold" style={{ color: 'var(--text-primary)' }}>{b.service?.name}</p>
                    <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{b.pet?.name} ({b.pet?.species}) · {b.customer?.name}</p>
                    {b.scheduledStart && <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}><Clock size={10} className="inline mr-1" />{new Date(b.scheduledStart).toLocaleString('en-IN', { hour: '2-digit', minute: '2-digit', day: 'numeric', month: 'short' })}</p>}
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <span className="badge text-xs" style={{ background: s.bg, color: s.color }}>{b.status.replace('_', ' ')}</span>
                    {b.liveStreamEnabled && <span className="badge text-xs" style={{ background: 'rgba(139,92,246,0.1)', color: '#8B5CF6' }}>📹 Live</span>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Session Detail Panel */}
      {selected && (
        <div className="modal-overlay" onClick={() => setSelected(null)}>
          <div className="modal-content max-w-lg overflow-y-auto max-h-[90vh]" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-outfit text-xl font-bold" style={{ color: 'var(--text-primary)' }}>{selected.service?.name}</h2>
              <button onClick={() => setSelected(null)} className="text-gray-400">✕</button>
            </div>

            <div className="grid grid-cols-2 gap-3 text-sm mb-4">
              <div className="p-3 rounded-xl" style={{ background: 'var(--bg-secondary)' }}>
                <p className="text-xs mb-1" style={{ color: 'var(--text-secondary)' }}>PET</p>
                <p className="font-semibold" style={{ color: 'var(--text-primary)' }}>{selected.pet?.name}</p>
                <p style={{ color: 'var(--text-secondary)' }}>{selected.pet?.breed || selected.pet?.species}</p>
              </div>
              <div className="p-3 rounded-xl" style={{ background: 'var(--bg-secondary)' }}>
                <p className="text-xs mb-1" style={{ color: 'var(--text-secondary)' }}>CUSTOMER</p>
                <p className="font-semibold" style={{ color: 'var(--text-primary)' }}>{selected.customer?.name}</p>
                <p style={{ color: 'var(--text-secondary)' }}>{selected.customer?.phone}</p>
              </div>
            </div>

            {selected.pet?.allergies && (
              <div className="p-3 rounded-xl mb-4 border" style={{ background: 'rgba(239,68,68,0.05)', borderColor: 'rgba(239,68,68,0.2)' }}>
                <p className="text-xs font-semibold mb-1 text-red-500">⚠️ ALLERGIES / PRECAUTIONS</p>
                <p className="text-sm" style={{ color: 'var(--text-primary)' }}>{selected.pet.allergies}</p>
              </div>
            )}

            {/* Status Actions */}
            <div className="mb-4">
              <p className="text-xs font-semibold mb-2" style={{ color: 'var(--text-secondary)' }}>UPDATE STATUS</p>
              <div className="flex flex-wrap gap-2">
                {['CONFIRMED', 'IN_PROGRESS', 'COMPLETED'].map(s => (
                  <button key={s} onClick={() => updateBookingStatus(selected.id, s)}
                    className="px-3 py-1.5 rounded-xl text-xs font-semibold transition-all hover:opacity-90"
                    style={{ background: STATUS_COLORS[s]?.bg, color: STATUS_COLORS[s]?.color, border: `1px solid ${STATUS_COLORS[s]?.color}40` }}>
                    {s.replace('_', ' ')}
                  </button>
                ))}
              </div>
            </div>

            {/* Live Stream Panel */}
            {selected.liveStreamEnabled && (
              <div className="mb-4">
                {!showStream ? (
                  <button onClick={() => setShowStream(true)} className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-bold text-white" style={{ background: 'linear-gradient(135deg, #EF4444, #8B5CF6)' }}>
                    <Radio size={14} />
                    {selected.liveStreamStatus === 'LIVE' ? '🔴 Manage Live Stream' : 'Start Live Stream'}
                  </button>
                ) : (
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <p className="text-xs font-semibold" style={{ color: 'var(--text-secondary)' }}>LIVE STREAM</p>
                      <button onClick={() => setShowStream(false)} className="text-xs" style={{ color: 'var(--text-secondary)' }}>Hide</button>
                    </div>
                    <LiveStreamHost booking={selected}
                      onStreamStatusChange={(st, upd) => {
                        if (upd) { setSelected({ ...selected, liveStreamStatus: st }); setBookings(prev => prev.map(b => b.id === selected.id ? { ...b, liveStreamStatus: st } : b)); }
                        addToast(st === 'LIVE' ? '🔴 Stream started!' : 'Stream ended.', st === 'LIVE' ? 'success' : 'info');
                      }}
                    />
                  </div>
                )}
              </div>
            )}

            {/* Service Log */}
            <div>
              <p className="text-xs font-semibold mb-3 flex items-center gap-1" style={{ color: 'var(--text-secondary)' }}><FileText size={12} /> SESSION LOG {existingLog && <span className="text-green-500">(Saved)</span>}</p>
              {/* Before/After Photos */}
              <div className="grid grid-cols-2 gap-3 mb-3">
                {['before', 'after'].map(type => {
                  const url = logForm[type === 'before' ? 'beforePhotoUrl' : 'afterPhotoUrl'];
                  const inputRef = type === 'before' ? beforeInputRef : afterInputRef;
                  return (
                    <div key={type}>
                      <p className="text-xs font-semibold mb-1 capitalize" style={{ color: 'var(--text-secondary)' }}>{type} Photo</p>
                      <input ref={inputRef} type="file" accept="image/*" className="hidden"
                        onChange={e => e.target.files[0] && uploadPhoto(e.target.files[0], type)}
                      />
                      {url ? (
                        <div className="relative group rounded-xl overflow-hidden" style={{ aspectRatio: '4/3' }}>
                          <img src={url} alt={`${type} photo`} className="w-full h-full object-cover" />
                          <button
                            onClick={() => inputRef.current?.click()}
                            className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-white text-xs font-semibold"
                          ><Camera size={16} className="mr-1" /> Change</button>
                        </div>
                      ) : (
                        <button
                          onClick={() => inputRef.current?.click()}
                          disabled={uploadingPhoto === type}
                          className="w-full rounded-xl flex flex-col items-center justify-center gap-1 py-4 transition-all"
                          style={{ background: 'var(--bg-secondary)', border: '2px dashed var(--border-color)', color: 'var(--text-secondary)', aspectRatio: '4/3' }}
                        >
                          {uploadingPhoto === type ? <Loader2 size={20} className="animate-spin text-petfolk-orange" /> : <Image size={20} />}
                          <span className="text-xs">{uploadingPhoto === type ? 'Uploading...' : 'Add Photo'}</span>
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="space-y-3">
                <textarea
                  placeholder="Grooming notes (what was done, coat condition, etc.)"
                  rows={3}
                  className="w-full rounded-xl p-3 text-sm resize-none"
                  style={{ background: 'var(--bg-secondary)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' }}
                  value={logForm.groomingNotes}
                  onChange={e => setLogForm(f => ({ ...f, groomingNotes: e.target.value }))}
                />
                <textarea
                  placeholder="Behavior observations (calm, anxious, cooperative)"
                  rows={2}
                  className="w-full rounded-xl p-3 text-sm resize-none"
                  style={{ background: 'var(--bg-secondary)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' }}
                  value={logForm.behaviorObservations}
                  onChange={e => setLogForm(f => ({ ...f, behaviorObservations: e.target.value }))}
                />
                <input
                  placeholder="Products used (comma separated)"
                  className="w-full rounded-xl p-3 text-sm"
                  style={{ background: 'var(--bg-secondary)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' }}
                  value={logForm.productsUsed}
                  onChange={e => setLogForm(f => ({ ...f, productsUsed: e.target.value }))}
                />
                <input
                  placeholder="Next grooming recommendation (e.g., 3 weeks)"
                  className="w-full rounded-xl p-3 text-sm"
                  style={{ background: 'var(--bg-secondary)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' }}
                  value={logForm.nextGroomingRecommendation}
                  onChange={e => setLogForm(f => ({ ...f, nextGroomingRecommendation: e.target.value }))}
                />
                <button
                  onClick={saveLog}
                  disabled={savingLog}
                  className="w-full py-2.5 rounded-xl text-sm font-bold text-white flex items-center justify-center gap-2"
                  style={{ background: 'var(--petfolk-orange)' }}
                  data-testid="save-service-log"
                >
                  {savingLog ? <><Loader2 size={14} className="animate-spin" /> Saving...</> : <><CheckCircle2 size={14} /> Save Log</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
